import React from 'react';
import { X, CheckCircle2, AlertTriangle, TrendingUp, Shield, Lightbulb } from 'lucide-react';

interface ProductAnalysis {
  strengths: string[];
  risks: string[];
  overall_assessment: string;
  score: number;
  recommendations: string[];
  product_code?: string;
}

interface Props {
  analysis: ProductAnalysis;
  onClose: () => void;
  productLabel?: string;
}

const PRODUCT_LABELS: Record<string, string> = {
  pup_grant: 'Dotacja PUP', wsparcie_na_starcie: 'Wsparcie na Starcie',
  kfs: 'KFS Szkolenia', dotacje_rozwojowe: 'Dotacje Rozwojowe',
  turnusy: 'Turnusy Rehabilitacyjne', wycieczki: 'Wycieczki eNHa TRIP',
  zus: 'Pomoc ZUS', bhp: 'Szkolenia BHP', czyste_powietrze: 'Czyste Powietrze',
  hutnik_biznes: 'Hutnik Business', marketing_beauty: 'Marketing B2B',
  gastro_consulting: 'Gastro Consulting', uslugi_ksiegowe: 'Usługi Księgowe',
  granty_fpn: 'Granty FPN', pozyczki_rozwojowe: 'Pożyczki Rozwojowe',
};

function getScoreColor(score: number): string {
  if (score >= 80) return 'text-emerald-600';
  if (score >= 60) return 'text-blue-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-red-600';
}

function getScoreBg(score: number): string {
  if (score >= 80) return 'bg-emerald-100 border-emerald-300';
  if (score >= 60) return 'bg-blue-100 border-blue-300';
  if (score >= 40) return 'bg-amber-100 border-amber-300';
  return 'bg-red-100 border-red-300';
}

function getVerdict(score: number): string {
  if (score >= 80) return 'Bardzo dobra ocena';
  if (score >= 60) return 'Dobra ocena';
  if (score >= 40) return 'Ocena warunkowa';
  return 'Wymaga poprawy';
}

export function ProductAnalysisDisplay({ analysis, onClose, productLabel }: Props) {
  const label = productLabel || PRODUCT_LABELS[analysis.product_code || ''] || 'Produkt';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#0f2a4d] to-[#1e4d7a] text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Analiza AI — {label}</h2>
            <p className="text-sm text-blue-200 mt-1">Automatyczna ocena na podstawie danych CRM</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Score */}
          <div className={`text-center p-6 rounded-xl border-2 ${getScoreBg(analysis.score)}`}>
            <div className={`text-5xl font-black ${getScoreColor(analysis.score)}`}>
              {analysis.score}<span className="text-2xl">/100</span>
            </div>
            <div className={`text-lg font-semibold mt-1 ${getScoreColor(analysis.score)}`}>
              {getVerdict(analysis.score)}
            </div>
          </div>

          {/* Assessment */}
          <div className="bg-gray-50 rounded-xl p-5 border">
            <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
              <TrendingUp size={18} className="text-blue-600" />
              Ogólna ocena
            </h3>
            <p className="text-gray-700 leading-relaxed">{analysis.overall_assessment}</p>
          </div>

          {/* Strengths */}
          {analysis.strengths?.length > 0 && (
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
              <h3 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                <CheckCircle2 size={18} className="text-emerald-600" />
                Mocne strony ({analysis.strengths.length})
              </h3>
              <ul className="space-y-2">
                {analysis.strengths.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-emerald-900">
                    <span className="text-emerald-500 mt-0.5">✓</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risks */}
          {analysis.risks?.length > 0 && (
            <div className="bg-red-50 rounded-xl p-5 border border-red-200">
              <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                <AlertTriangle size={18} className="text-red-600" />
                Ryzyka ({analysis.risks.length})
              </h3>
              <ul className="space-y-2">
                {analysis.risks.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-red-900">
                    <span className="text-red-500 mt-0.5">⚠</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {analysis.recommendations?.length > 0 && (
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                <Lightbulb size={18} className="text-blue-600" />
                Rekomendacje ({analysis.recommendations.length})
              </h3>
              <ul className="space-y-2">
                {analysis.recommendations.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-blue-900">
                    <span className="bg-blue-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 rounded-b-2xl flex justify-between items-center">
          <span className="text-xs text-gray-400">Wygenerowano przez AI • Wynik może wymagać weryfikacji operatora</span>
          <button onClick={onClose} className="px-5 py-2 bg-[#0f2a4d] text-white rounded-lg hover:bg-[#1e4d7a] font-medium text-sm">
            Zamknij
          </button>
        </div>
      </div>
    </div>
  );
}
